import React, { useRef, useState } from 'react';
import moment from 'moment';
import { getPlanningDeletionReason } from './planningSchedule';
import {
    allocatePlanningLanes,
    createPlanningWeeks,
    filterPlanningEvents,
    getDailyPlanningMetrics,
    getPlanningWeekSummary,
    movePlanningEventToDate,
    parsePlanningTitle,
    getPlanningManpower,
    resizePlanningEventToDate,
} from './planningCalendar';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import './PlanningCalendar.css';
import PlanningFilters from './PlanningFilters.jsx';
import { Nav, Tab } from 'react-bootstrap';
import PlanningBoard from './PlanningBoard.jsx';
import PlanningAuditLog from './PlanningAuditLog.jsx';
import PlanningJobTooltip from './PlanningJobTooltip.jsx';
import { getBoardMachines } from './planningBoard';

const DAY_FORMAT = 'DD/MM/YYYY';
const MOVE_TYPE = 'planning-event-move';
const RESIZE_START_TYPE = 'planning-event-resize-start';
const RESIZE_END_TYPE = 'planning-event-resize-end';

function formatWeekDate(date) {
    return moment(date).format('ddd DD/MM');
}

function formatWeekLabel(week) {
    return `WEEK ${moment(week.startDate).isoWeek()} · ${formatWeekDate(week.startDate).toUpperCase()} – ${formatWeekDate(week.endDate).toUpperCase()}`;
}

function eventKey(event) {
    return event.jobID ?? event.oldId ?? event.id;
}

function getSundayColumn(week) {
    const index = week.dates.findIndex(date => moment(date).day() === 0);
    return index === -1 ? null : index + 1;
}

function MetricRow({ label, week, events, metric }) {
    return (
        <div className="planning-week__metric-row">
            <div className="planning-week__row-label">{label}</div>
            {week.dates.map(date => {
                const isSunday = moment(date).day() === 0;
                const values = getDailyPlanningMetrics(events, date);
                const value = metric === 'manpower' ? values.manpower : values.targetOutput;
                return (
                    <div
                        className={`planning-week__metric-cell${isSunday ? ' is-sunday' : ''}`}
                        key={date}
                        data-testid={isSunday ? 'planning-sunday-metric' : undefined}
                    >
                        {metric === 'targetOutput' && values.pending ? '—' : value ?? '—'}
                    </div>
                );
            })}
        </div>
    );
}

function SundayMask({ segment, week }) {
    if (segment.event.ot === true) return null;
    const sundayColumn = getSundayColumn(week);
    if (sundayColumn === null || sundayColumn < segment.startColumn || sundayColumn > segment.endColumn) {
        return null;
    }

    const span = segment.endColumn - segment.startColumn + 1;
    return (
        <span
            className="planning-job__sunday-mask"
            data-testid="planning-sunday-job-mask"
            aria-label="Sunday non-working time"
            style={{
                left: `${((sundayColumn - segment.startColumn) / span) * 100}%`,
                width: `${(1 / span) * 100}%`,
            }}
        />
    );
}

function DropDayCell({ date, column, isSunday, canCreate, onAddJob, onMoveJob, onResizeJob }) {
    const [, dropRef] = useDrop(() => ({
        accept: [MOVE_TYPE, RESIZE_START_TYPE, RESIZE_END_TYPE],
        drop: item => {
            try {
                if (item.mode === 'move' && onMoveJob) {
                    onMoveJob(item.event, movePlanningEventToDate(item.event, date));
                } else if (item.mode === 'resize-start' && onResizeJob) {
                    onResizeJob(item.event, resizePlanningEventToDate(item.event, 'start', date));
                } else if (item.mode === 'resize-end' && onResizeJob) {
                    onResizeJob(item.event, resizePlanningEventToDate(item.event, 'end', date));
                }
            } catch (_error) {
                // Invalid resize drops are rejected without changing the event.
            }
        },
    }), [date, onMoveJob, onResizeJob]);

    const canAddJob = canCreate && Boolean(onAddJob);
    const Background = canAddJob ? 'button' : 'span';
    return (
        <Background
            ref={dropRef}
            type={canAddJob ? 'button' : undefined}
            aria-label={canAddJob ? `Add job on ${moment(date).format(DAY_FORMAT)}` : undefined}
            onClick={canAddJob ? () => onAddJob(date) : undefined}
            style={{ gridColumn: column }}
            className={`planning-week__day-background${isSunday ? ' is-sunday' : ''}`}
            data-testid={isSunday ? 'planning-sunday' : undefined}
        />
    );
}

function JobSegment({ segment, week, onOpenJob, onDeleteJob, onMoveJob, onResizeJob }) {
    const isAnyDragging = useDragLayer(monitor => monitor.isDragging());
    const event = segment.event;
    const parsed = parsePlanningTitle(event.title);
    const span = segment.endColumn - segment.startColumn + 1;
    const [{ isDragging }, dragRef] = useDrag(() => ({
        type: MOVE_TYPE,
        canDrag: () => event.metricsPending !== true,
        item: { event, mode: 'move' },
        collect: monitor => ({ isDragging: monitor.isDragging() }),
    }), [event]);
    const [, resizeStartRef] = useDrag(() => ({
        type: RESIZE_START_TYPE,
        canDrag: () => event.metricsPending !== true,
        item: { event, mode: 'resize-start' },
    }), [event]);
    const [, resizeEndRef] = useDrag(() => ({
        type: RESIZE_END_TYPE,
        canDrag: () => event.metricsPending !== true,
        item: { event, mode: 'resize-end' },
    }), [event]);
    const segmentStyle = {
        gridColumn: `${segment.startColumn} / ${segment.endColumn + 1}`,
        top: `${segment.lane * 34 + 6}px`,
        backgroundColor: event.bgColor || '#1769d2',
        '--planning-job-span': span,
        opacity: isDragging ? 0.55 : 1,
    };

    const handleOpen = () => {
        if (onOpenJob) onOpenJob(event);
    };

    return (
        <div
            className="planning-job"
            ref={dragRef}
            style={segmentStyle}
        >
            <span
                ref={resizeStartRef}
                className="planning-job__resize-handle planning-job__resize-handle--start"
                aria-hidden="true"
            />
            <PlanningJobTooltip event={event} disabled={isAnyDragging}>
            <button
                type="button"
                className="planning-job__open"
                aria-label={`Open ${parsed.displayTitle || 'job'}`}
                onClick={handleOpen}
                onKeyDown={eventKeyDown => {
                    if (eventKeyDown.key === 'Enter' || eventKeyDown.key === ' ') {
                        return;
                    }

                    if (!eventKeyDown.altKey) return;
                    if (event.metricsPending === true) return;
                    const direction = eventKeyDown.key === 'ArrowRight' ? 1 : eventKeyDown.key === 'ArrowLeft' ? -1 : 0;
                    if (!direction) return;
                    eventKeyDown.preventDefault();

                    const targetDate = moment(eventKeyDown.shiftKey ? event.end : event.start)
                        .add(direction, 'days')
                        .format('YYYY-MM-DD');
                    try {
                        if (eventKeyDown.shiftKey && onResizeJob) {
                            onResizeJob(event, resizePlanningEventToDate(event, 'end', targetDate));
                        } else if (eventKeyDown.ctrlKey && onResizeJob) {
                            onResizeJob(event, resizePlanningEventToDate(event, 'start', targetDate));
                        } else if (onMoveJob) {
                            onMoveJob(event, movePlanningEventToDate(event, targetDate));
                        }
                    } catch (_error) {
                        // Keep the event unchanged when the keyboard resize would invert it.
                    }
                }}
            >
                {segment.continuesBefore && <span className="planning-job__continuation" aria-hidden="true">‹</span>}
                <span className="planning-job__title">{parsed.displayTitle || 'Untitled job'}</span>
                <span className="planning-job__badges">
                    {parsed.closed && <span className="planning-job__badge">CLOSED</span>}
                    <span className="planning-job__badge">{getPlanningManpower(event) ?? '—'} MP</span>
                    <span className="planning-job__details" aria-hidden="true">›</span>
                </span>
            </button>
            </PlanningJobTooltip>
            {onDeleteJob && (
                <button
                    type="button"
                    className="planning-job__delete"
                    disabled={Boolean(getPlanningDeletionReason(event))}
                    title={getPlanningDeletionReason(event) || 'Delete schedule'}
                    aria-label={`Delete ${parsed.displayTitle || 'job'}`}
                    onClick={clickEvent => {
                        clickEvent.stopPropagation();
                        onDeleteJob(event);
                    }}
                >
                    ×
                </button>
            )}
            <span
                ref={resizeEndRef}
                className="planning-job__resize-handle planning-job__resize-handle--end"
                aria-hidden="true"
            />
            <SundayMask segment={segment} week={week} />
        </div>
    );
}

function JobsCanvas({ week, events, machines, canCreate, onAddJob, onOpenJob, onDeleteJob, onMoveJob, onResizeJob }) {
    const segments = allocatePlanningLanes(events, week, machines);
    const laneCount = segments.reduce((maxLane, segment) => Math.max(maxLane, segment.lane + 1), 0);
    const canvasStyle = { minHeight: `${Math.max(72, laneCount * 34 + 12)}px` };
    const canvasNode = useRef(null);
    const [, canvasDropRef] = useDrop(() => ({
        accept: [MOVE_TYPE, RESIZE_START_TYPE, RESIZE_END_TYPE],
        drop: (item, monitor) => {
            if (monitor.didDrop() || !canvasNode.current) return;
            const offset = monitor.getClientOffset();
            const bounds = canvasNode.current.getBoundingClientRect();
            if (!offset || !bounds.width) return;
            const column = Math.max(0, Math.min(6, Math.floor(((offset.x - bounds.left) / bounds.width) * 7)));
            const date = week.dates[column];
            try {
                if (item.mode === 'move' && onMoveJob) {
                    onMoveJob(item.event, movePlanningEventToDate(item.event, date));
                } else if (item.mode === 'resize-start' && onResizeJob) {
                    onResizeJob(item.event, resizePlanningEventToDate(item.event, 'start', date));
                } else if (item.mode === 'resize-end' && onResizeJob) {
                    onResizeJob(item.event, resizePlanningEventToDate(item.event, 'end', date));
                }
            } catch (_error) {
                // Match day-cell drops: invalid resizes must not escape DnD cleanup.
            }
        },
    }), [week, onMoveJob, onResizeJob]);

    const setCanvasRef = node => {
        canvasNode.current = node;
        canvasDropRef(node);
    };

    return (
        <div className="planning-week__jobs-row">
            <div className="planning-week__row-label">Jobs</div>
            <div ref={setCanvasRef} className="planning-week__jobs-canvas" style={canvasStyle}>
                {week.dates.map((date, index) => (
                    <DropDayCell
                        key={date}
                        column={index + 1}
                        date={date}
                        isSunday={moment(date).day() === 0}
                        canCreate={canCreate}
                        onAddJob={onAddJob}
                        onMoveJob={onMoveJob}
                        onResizeJob={onResizeJob}
                    />
                ))}
                {segments.map(segment => (
                    <JobSegment
                        key={`${eventKey(segment.event)}-${week.startDate}`}
                        segment={segment}
                        week={week}
                        onOpenJob={onOpenJob}
                        onDeleteJob={onDeleteJob}
                        onMoveJob={onMoveJob}
                        onResizeJob={onResizeJob}
                    />
                ))}
            </div>
        </div>
    );
}

function WeekPanel({ week, events, machines, canCreate, onAddJob, onOpenJob, onDeleteJob, onMoveJob, onResizeJob }) {
    const summary = getPlanningWeekSummary(events, week);
    const jobLabel = `${summary.jobCount} ${summary.jobCount === 1 ? 'job' : 'jobs'}`;

    return (
        <section className="planning-week" aria-labelledby={`planning-week-${week.index}`}>
            <div className="planning-week__summary">
                <h2 id={`planning-week-${week.index}`}>{formatWeekLabel(week)}</h2>
                <span>{jobLabel} · {summary.manpowerDays ?? '—'} manpower-days planned</span>
            </div>
            <div className="planning-week__table">
                <div className="planning-week__date-row">
                    <div className="planning-week__row-label">Date</div>
                    {week.dates.map(date => {
                        const isSunday = moment(date).day() === 0;
                        return (
                            <div
                                key={date}
                                className={`planning-week__date-cell${isSunday ? ' is-sunday' : ''}`}
                                data-testid="planning-date-header"
                            >
                                <span>{moment(date).format('ddd').toUpperCase()}</span>
                                <strong>{moment(date).format(DAY_FORMAT)}</strong>
                            </div>
                        );
                    })}
                </div>
                <JobsCanvas
                    week={week}
                    events={events}
                    machines={machines}
                    canCreate={canCreate}
                    onAddJob={onAddJob}
                    onOpenJob={onOpenJob}
                    onDeleteJob={onDeleteJob}
                    onMoveJob={onMoveJob}
                    onResizeJob={onResizeJob}
                />
                <MetricRow label="Manpower" week={week} events={events} metric="manpower" />
                <MetricRow label="Target output" week={week} events={events} metric="targetOutput" />
            </div>
        </section>
    );
}

function CalendarStatus({ error, onRetry }) {
    return (
        <div className="planning-calendar__status" role="status">
            <span>{error}</span>
            {onRetry && <button type="button" onClick={onRetry}>Retry</button>}
        </div>
    );
}

function PlanningCalendarContent({
    startDate,
    events = [],
    machines = [],
    dateControls,
    canCreate = false,
    initialLoading = false,
    refreshing = false,
    error = '',
    onRetry,
    onAddJob,
    onAddBoardJob,
    onOpenJob,
    onDeleteJob,
    onMoveJob,
    onResizeJob,
    onResizeBoardJob,
}) {
    const [activeView, setActiveView] = useState('overview');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [machineFilter, setMachineFilter] = useState('');
    const weeks = createPlanningWeeks(startDate);
    const visibleEvents = filterPlanningEvents(events, machines, categoryFilter, machineFilter);

    return (
        <section
            className="planning-calendar"
            aria-busy={initialLoading || refreshing}
            aria-label="Planning calendar"
        >
            <Tab.Container id="planning-views" activeKey={activeView} onSelect={setActiveView}>
                <div className="planning-overview__header">
                    <Nav className="planning-view-tabs" variant="tabs" aria-label="Planning views">
                        <Nav.Item><Nav.Link eventKey="overview">Planning Overview</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="board">Planning Board</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="audit">Audit Log</Nav.Link></Nav.Item>
                    </Nav>
                </div>
            {activeView === 'audit' ? (
                <Tab.Content><Tab.Pane eventKey="audit">
                    <PlanningAuditLog />
                </Tab.Pane></Tab.Content>
            ) : <>
            <div className="planning-overview__controls">
                {dateControls}
                <PlanningFilters
                    machines={machines}
                    category={categoryFilter}
                    machine={machineFilter}
                    onCategoryChange={category => {
                        setCategoryFilter(category);
                        setMachineFilter('');
                    }}
                    onMachineChange={setMachineFilter}
                />
            </div>
            {error && <CalendarStatus error={error} onRetry={onRetry} />}
            {initialLoading && events.length === 0 ? (
                <div className="planning-calendar__loading" role="status">Loading planning events…</div>
            ) : activeView === 'board' ? (
                <Tab.Content><Tab.Pane eventKey="board">
                    <PlanningBoard boards={getBoardMachines(machines, events, categoryFilter, machineFilter)} week={weeks[0]} canCreate={canCreate} onAddJob={onAddBoardJob} onOpenJob={onOpenJob} onDeleteJob={onDeleteJob} onMoveJob={onMoveJob} onResizeJob={onResizeBoardJob} />
                </Tab.Pane></Tab.Content>
            ) : <Tab.Content><Tab.Pane eventKey="overview">{weeks.map(week => (
                <WeekPanel
                    key={week.startDate}
                    week={week}
                    events={visibleEvents}
                    machines={machines}
                    canCreate={canCreate}
                    onAddJob={onAddJob}
                    onOpenJob={onOpenJob}
                    onDeleteJob={onDeleteJob}
                    onMoveJob={onMoveJob}
                    onResizeJob={onResizeJob}
                />
            ))}</Tab.Pane></Tab.Content>}
            </>}
            </Tab.Container>
        </section>
    );
}

export default function PlanningCalendar(props) {
    return (
        <DndProvider backend={HTML5Backend}>
            <PlanningCalendarContent {...props} />
        </DndProvider>
    );
}
